import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { aiCertificationAPI } from '../../services/api';
import { ArrowLeft, Save, Plus, X } from 'lucide-react';

const AICertificationForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [form, setForm] = useState({
    investor_name: '',
    pan: '',
    investor_type: 'Individual',
    is_resident: true,
    address: '',
    city: '',
    state: '',
    pin_zip: '',
    email: '',
    mobile: '',
  });
  const [investorTypes, setInvestorTypes] = useState([]);
  const [newType, setNewType] = useState('');
  const [showAddType, setShowAddType] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadTypes();
    if (isEdit) loadRecord();
  }, [id]);

  const loadTypes = async () => {
    try {
      const res = await aiCertificationAPI.getInvestorTypes();
      setInvestorTypes(res.data.all_types || []);
    } catch (err) { console.error(err); }
  };

  const loadRecord = async () => {
    setLoading(true);
    try {
      const res = await aiCertificationAPI.getById(id);
      setForm({
        investor_name: res.data.investor_name || '',
        pan: res.data.pan || '',
        investor_type: res.data.investor_type || 'Individual',
        is_resident: res.data.is_resident !== false,
        address: res.data.address || '',
        city: res.data.city || '',
        state: res.data.state || '',
        pin_zip: res.data.pin_zip || '',
        email: res.data.email || '',
        mobile: res.data.mobile || '',
      });
    } catch (err) {
      alert('Failed to load record');
      navigate('/ai-certification');
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    const errs = {};
    if (!form.investor_name.trim()) errs.investor_name = 'Required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      if (isEdit) {
        await aiCertificationAPI.update(id, form);
      } else {
        await aiCertificationAPI.create(form);
      }
      navigate('/ai-certification');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleAddType = async () => {
    if (!newType.trim()) return;
    try {
      await aiCertificationAPI.addInvestorType(newType.trim());
      setInvestorTypes(prev => [...prev, newType.trim()]);
      setForm(prev => ({ ...prev, investor_type: newType.trim() }));
      setNewType('');
      setShowAddType(false);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to add type');
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Loading...</div>;

  return (
    <div className="p-6 max-w-3xl mx-auto" data-testid="ai-certification-form-page">
      <button onClick={() => navigate('/ai-certification')} className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4" data-testid="back-btn">
        <ArrowLeft size={16} className="mr-1" /> Back to List
      </button>

      <h1 className="text-2xl font-bold text-gray-900 mb-6" data-testid="form-title">
        {isEdit ? 'Edit Investor' : 'Add Investor for AI Certification'}
      </h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Investor Name + PAN */}
        <div className="bg-white border rounded-lg p-6 space-y-4">
          <h2 className="text-base font-semibold text-gray-800 border-b pb-2">Basic Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name of Investor *</label>
              <input type="text" value={form.investor_name} onChange={e => handleChange('investor_name', e.target.value)}
                className={`w-full border rounded-lg px-3 py-2 text-sm ${errors.investor_name ? 'border-red-400' : ''}`}
                placeholder="Enter investor name" data-testid="input-investor-name" />
              {errors.investor_name && <p className="text-xs text-red-500 mt-1">{errors.investor_name}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">PAN</label>
              <input type="text" value={form.pan} onChange={e => handleChange('pan', e.target.value.toUpperCase())}
                className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="e.g. ABCDE1234F"
                maxLength={10} data-testid="input-pan" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <div className="flex gap-2">
                <select value={form.investor_type} onChange={e => handleChange('investor_type', e.target.value)}
                  className="flex-1 border rounded-lg px-3 py-2 text-sm" data-testid="select-investor-type">
                  {investorTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <button type="button" onClick={() => setShowAddType(true)}
                  className="px-2 py-2 border rounded-lg text-gray-500 hover:text-blue-600 hover:border-blue-300" data-testid="add-type-btn" title="Add custom type">
                  <Plus size={16} />
                </button>
              </div>
              {showAddType && (
                <div className="flex gap-2 mt-2">
                  <input type="text" value={newType} onChange={e => setNewType(e.target.value)}
                    className="flex-1 border rounded-lg px-3 py-2 text-sm" placeholder="New type name"
                    data-testid="input-new-type" autoFocus />
                  <button type="button" onClick={handleAddType} className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700" data-testid="save-type-btn">Add</button>
                  <button type="button" onClick={() => { setShowAddType(false); setNewType(''); }} className="p-2 text-gray-400 hover:text-gray-600"><X size={16} /></button>
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Resident Status</label>
              <div className="flex items-center gap-4 mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="resident" checked={form.is_resident === true}
                    onChange={() => handleChange('is_resident', true)} className="text-blue-600" data-testid="radio-resident" />
                  <span className="text-sm text-gray-700">Resident</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="resident" checked={form.is_resident === false}
                    onChange={() => handleChange('is_resident', false)} className="text-blue-600" data-testid="radio-non-resident" />
                  <span className="text-sm text-gray-700">Non-Resident</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Address */}
        <div className="bg-white border rounded-lg p-6 space-y-4">
          <h2 className="text-base font-semibold text-gray-800 border-b pb-2">Address</h2>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
            <input type="text" value={form.address} onChange={e => handleChange('address', e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Street address" data-testid="input-address" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
              <input type="text" value={form.city} onChange={e => handleChange('city', e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="City" data-testid="input-city" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
              <input type="text" value={form.state} onChange={e => handleChange('state', e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="State" data-testid="input-state" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pin/Zip</label>
              <input type="text" value={form.pin_zip} onChange={e => handleChange('pin_zip', e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Pin/Zip code" data-testid="input-pin-zip" />
            </div>
          </div>
        </div>

        {/* Contact */}
        <div className="bg-white border rounded-lg p-6 space-y-4">
          <h2 className="text-base font-semibold text-gray-800 border-b pb-2">Contact Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email ID</label>
              <input type="email" value={form.email} onChange={e => handleChange('email', e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="email@example.com" data-testid="input-email" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number</label>
              <input type="text" value={form.mobile} onChange={e => handleChange('mobile', e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="+91 98765 43210" data-testid="input-mobile" />
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-3">
          <button type="button" onClick={() => navigate('/ai-certification')}
            className="px-4 py-2 border rounded-lg text-sm text-gray-700 hover:bg-gray-50" data-testid="cancel-btn">Cancel</button>
          <button type="submit" disabled={saving}
            className="inline-flex items-center px-5 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50" data-testid="submit-btn">
            <Save size={16} className="mr-2" /> {saving ? 'Saving...' : (isEdit ? 'Update' : 'Create')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AICertificationForm;
